/**
 * Greeting Agent for Ameen Insurance Assistant
 *
 * Entry point: greets user, presents options (create policy, open claim, ask question),
 * routes by intent to policy_agent, claim_agent, or faq_agent.
 *
 * TOOLS (match backend):
 * - go_to_policy_agent, go_to_claim_agent, go_to_faq_agent
 * - search_faq (optional during greeting)
 */

import { AgentNode, AgentContext, ToolCallResult, VoiceGender, UIStateManager } from '../types';
import { noEntryRequirements } from '../guardrails';
import { logger } from '../../utils/logger';
import { handleSearchFAQ } from './RouterAgent';

export function createGreetingAgent(voiceGender: VoiceGender): AgentNode {
  return {
    id: 'greeting_agent',
    name: 'Greeting Agent',
    description: 'Entry point: greet, present options, route to policy/claim/FAQ by intent',

    entryGuardrails: noEntryRequirements(),

    exitGuardrails: {
      transitions: new Map([
        ['go_to_policy', { target: { agentId: 'policy_agent' }, guardrails: [] }],
        ['go_to_claim', { target: { agentId: 'claim_agent' }, guardrails: [] }],
        ['go_to_faq', { target: { agentId: 'faq_agent' }, guardrails: [] }],
      ]),
      fallback: { agentId: 'router_agent' },
    },

    initialPrompt: 'ابدأ الترحيب.',

    handleToolCall: async (
      toolName: string,
      args: Record<string, any>,
      context: AgentContext,
      uiState?: UIStateManager
    ): Promise<ToolCallResult> => {
      logger.tool('[GreetingAgent] Tool called:', toolName, args);

      switch (toolName) {
        case 'go_to_policy_agent':
          logger.info('[GreetingAgent] Transitioning to policy_agent');
          return {
            toolName,
            args,
            result: { success: true, message: 'Routing to policy creation' },
            triggersTransition: true,
            transitionTo: 'policy_agent',
            transitionDelayMs: -1,
          };

        case 'go_to_claim_agent':
          logger.info('[GreetingAgent] Transitioning to claim_agent');
          return {
            toolName,
            args,
            result: { success: true, message: 'Routing to claim filing' },
            triggersTransition: true,
            transitionTo: 'claim_agent',
            transitionDelayMs: -1,
          };

        case 'go_to_faq_agent':
          logger.info('[GreetingAgent] Transitioning to faq_agent');
          return {
            toolName,
            args,
            result: { success: true, message: 'Routing to FAQ' },
            triggersTransition: true,
            transitionTo: 'faq_agent',
            transitionDelayMs: -1,
          };

        case 'search_faq':
          return await handleSearchFAQ(args, toolName);

        default:
          logger.warn('[GreetingAgent] Unknown tool:', toolName);
          return {
            toolName,
            args,
            result: { success: false, error: `Unknown tool: ${toolName}` },
          };
      }
    },

    onActivate: async (context: AgentContext) => {
      logger.info('[GreetingAgent] Activated');
    },

    onDeactivate: async (context: AgentContext) => {
      logger.info('[GreetingAgent] Deactivated');
    },
  };
}
